import { Injectable } from '@angular/core';
import { HelperService } from '@sb-tools/typescript/services/helper.service';
import { WindowRef } from '@sb-tools/typescript/services/window-ref.service';
import { CookieService } from '@sb-tools/typescript/authentication/cookie.service';
import { CommonDataService } from '@sb-tools/typescript/services/common-data.service';
import { finalize, take } from 'rxjs/operators';
import { ZissonInteractCategoryChangeStatus } from '../enums/zisson-interact-category-change-status.enum';
import { BsModalService } from 'ngx-bootstrap/modal';
import { ZissonInteractService } from './zisson-interact.service';
import { ZissonInteractCategoryChangesResult } from '../../core/models/zisson-interact-category-changes-result.model';
import { CategoryChangeWarningModalComponent } from '../../shared/modal-windows/category-change-warning-modal/category-change-warning-modal.component';

@Injectable({
    providedIn: 'root',
})
export class CategoryChangeWarningStatusService {
    private isLoadingCategoryChangeStatus = false;
    // todo: need to rename, this name is too shared
    private key = 'doNotShowConfirmationModalStatus';

    constructor(
        private windowRef: WindowRef,
        private cookieService: CookieService,
        private commonDataService: CommonDataService,
        private bsModalService: BsModalService,
        private zissonInteractService: ZissonInteractService,
    ) {}

    checkCategoryChangeStatus(ticketIds: number[], openCategoriesModal: () => void, isBulkOperation = false): void {
        if (this.isLoadingCategoryChangeStatus) {
            return;
        }

        if (
            !this.commonDataService.customerSettings.isZissonIntegrationEnabled ||
            !this.isNeedToShowConfirmationModal()
        ) {
            openCategoriesModal();
            return;
        } else {
            this.isLoadingCategoryChangeStatus = true;

            const getCategoryChangeStatus$ = isBulkOperation
                ? this.zissonInteractService.getCategoryChangesStatus(ticketIds)
                : this.zissonInteractService.getCategoryChangeStatus(ticketIds[0]);

            getCategoryChangeStatus$
                .pipe(
                    take(1),
                    finalize(() => {
                        setTimeout(() => {
                            this.isLoadingCategoryChangeStatus = false;
                        }, 0);
                    }),
                )
                .subscribe((result: ZissonInteractCategoryChangesResult) => {
                    const ticketIdsAllowedWithWarning = result.categoryChangeStatuses?.find((changeStatus) => {
                        return changeStatus.status === ZissonInteractCategoryChangeStatus.allowedWithWarning;
                    })?.ticketIds;
                    switch (result.status) {
                        case ZissonInteractCategoryChangeStatus.allowedWithoutRestrictions:
                            openCategoriesModal();
                            break;
                        case ZissonInteractCategoryChangeStatus.allowedWithWarning:
                            if (this.isNeedToShowConfirmationModal()) {
                                const initialState = {
                                    ticketIds: ticketIdsAllowedWithWarning,
                                    confirmCallback: () => {
                                        return openCategoriesModal();
                                    },
                                };
                                this.bsModalService.show(CategoryChangeWarningModalComponent, {
                                    initialState,
                                });
                            } else {
                                return openCategoriesModal();
                            }
                            break;
                    }
                });
        }
    }

    isNeedToShowConfirmationModal(): boolean {
        let data;

        if (HelperService.isLocalStorageNameSupported()) {
            data = this.windowRef.nativeWindow.localStorage.getItem(this.key);
        } else {
            data = this.cookieService.getCookie(this.key);
        }

        if (data) {
            try {
                const parsedCommunitiesList = JSON.parse(data);

                return !parsedCommunitiesList.includes(this.commonDataService.communityName);
            } catch {
                return true;
            }
        } else {
            return true;
        }
    }

    saveModalDisplayStatus(): void {
        let storedData;

        if (HelperService.isLocalStorageNameSupported()) {
            storedData = this.windowRef.nativeWindow.localStorage.getItem(this.key);
        } else {
            storedData = this.cookieService.getCookie(this.key);
        }

        if (storedData) {
            try {
                storedData = JSON.parse(storedData);

                storedData.push(this.commonDataService.communityName);
            } catch {
                storedData = [this.commonDataService.communityName];
            }
        } else {
            storedData = [this.commonDataService.communityName];
        }

        if (HelperService.isLocalStorageNameSupported()) {
            this.windowRef.nativeWindow.localStorage.setItem(this.key, JSON.stringify(storedData));
        } else {
            this.cookieService.setCookie(this.key, JSON.stringify(storedData));
        }
    }
}
